import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Chip, Collapse, Divider, IconButton, MenuItem, Paper, Stack, TextField, Typography, Button, ButtonGroup } from '@mui/material';
import React, { useState } from 'react';
import { getListOfPeriods, getPeriodName, makePeriod } from '../../utils/rebatePeriods';
import PeriodCard from './PeriodCard';

const PERIOD_OPTIONS = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' },
    { value: 'contract_term', label: 'Contract Term' },
    { value: 'custom', label: 'Custom' },
    { value: 'unknown', label: 'Unknown' },
];

const REBATE_PROGRAM_TYPE_OPTIONS = [
    { value: 'per_unit_cashback', label: 'Per-Unit Cashback' },
    { value: 'threshold_milestone', label: 'Threshold Milestone' },
    { value: 'growth_dollars', label: 'Growth Dollars' },
    { value: 'mixed_threshold', label: 'Mixed Threshold' },
    { value: 'custom', label: 'Custom' },
    { value: 'unknown', label: 'Unknown' },
];

const REBATE_VALUE_UNIT_OPTIONS = [
    {value: 'flat_rebate_value', label: 'Flat $'},
    {value: 'percentage_spend', label: '% of Spend'},
    {value: 'per_unit_rebate_value', label: '$ Per Unit'}
];

const MANUAL_PERIOD_TYPES = ['custom', 'unknown'];

function RebateCard({ rebate, onChange, onDelete }) {
    const [expanded, setExpanded] = useState(true);
    const [rebateUnit, setRebateUnit] = useState(rebate.rebateValueUnit ?? REBATE_VALUE_UNIT_OPTIONS[0].value);
    const canRemovePeriod = Array.isArray(rebate.periods) && rebate.periods.length > 0;

    const handleRebateUnitChange = (nextUnit) => {
        setRebateUnit(nextUnit);
        onChange({ ...rebate, rebateValueType: nextUnit });
    };

    const handleAddPeriod = () => {
        const existingPeriods = Array.isArray(rebate.periods) ? rebate.periods : [];
        const lastPeriod = existingPeriods[existingPeriods.length - 1] ?? {};
        const newPeriod = makePeriod({
            name: `Custom Period ${existingPeriods.length + 1}`,
            startDate: lastPeriod.startDate ?? rebate.startDate ?? '',
            endDate: lastPeriod.endDate ?? rebate.endDate ?? '',
        });

        onChange({
            ...rebate,
            periodType: MANUAL_PERIOD_TYPES.includes(rebate.periodType) ? rebate.periodType : 'custom',
            periods: [...existingPeriods, newPeriod],
        });
    };

    const handleRemovePeriod = () => {
        const existingPeriods = Array.isArray(rebate.periods) ? rebate.periods : [];

        if (existingPeriods.length === 0) {
            return;
        }

        onChange({
            ...rebate,
            periods: existingPeriods.slice(0, -1),
        });
    };

    const handlePeriodTypeChange = (nextType) => {
        const currentType = rebate.periodType ?? '';
        const nextIsManual = MANUAL_PERIOD_TYPES.includes(nextType);

        if (!nextIsManual) {
            const existing = Array.isArray(rebate.periods) ? rebate.periods : [];
            const rows = getListOfPeriods(rebate.startDate, rebate.endDate, nextType);
            const periods = rows.map((row, index) => {
                const prev = existing[index] ?? existing[0] ?? {};
                return makePeriod({
                    name: getPeriodName(nextType, index),
                    startDate: row.startDate,
                    endDate: row.endDate,
                    formulaDescription: prev.formulaDescription ?? '',
                    tiers: prev.tiers,
                });
            });

            onChange({ ...rebate, periodType: nextType, periods });
            return;
        }

        const switchToManual = !MANUAL_PERIOD_TYPES.includes(currentType);

        if (!switchToManual) {
            onChange({ ...rebate, periodType: nextType });
            return;
        }

        const existing = Array.isArray(rebate.periods) ? rebate.periods : [];
        const rows = getListOfPeriods(rebate.startDate, rebate.endDate, currentType);
        const source = rows.length ? rows : existing;

        const periods = (source.length ? source : [{}]).map((row, index) => {
            const prev = existing[index] ?? existing[0] ?? {};
            return makePeriod({
                name: prev.name ?? getPeriodName(currentType, index),
                startDate: prev.startDate ?? row.startDate ?? '',
                endDate: prev.endDate ?? row.endDate ?? '',
                formulaDescription: prev.formulaDescription ?? '',
                tiers: prev.tiers,
            });
        });

        onChange({ ...rebate, periodType: nextType, periods });
    };

    return (
        <Paper elevation={1} sx={{ borderRadius: 2 }}>
            {/* Collapsed header */}
            <Stack
                direction='row'
                alignItems='center'
                justifyContent='space-between'
                px={2.5}
                py={1.75}
                sx={{ cursor: 'pointer' }}
                onClick={() => setExpanded(prev => !prev)}
            >
                <Stack direction='row' spacing={1.5} alignItems='center'>
                    <Typography variant='heavy' sx={{ fontSize: '15px' }}>
                        {rebate.name || <Box component='span' sx={{ color: 'subtext.main', fontWeight: 400 }}>Untitled Rebate</Box>}
                    </Typography>
                    {rebate.periodType && (
                        <Chip
                            label={PERIOD_OPTIONS.find(o => o.value === rebate.periodType)?.label ?? rebate.periodType}
                            size='small'
                            sx={{ backgroundColor: 'background.neutral', fontSize: '12px' }}
                        />
                    )}
                </Stack>
                <Stack direction='row' alignItems='center' spacing={0.5}>
                    <IconButton
                        size='small'
                        aria-label='Delete rebate'
                        onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    >
                        <DeleteOutlineIcon fontSize='small' />
                    </IconButton>
                    <IconButton size='small' aria-label={expanded ? 'Collapse rebate' : 'Expand rebate'} aria-expanded={expanded} onClick={(e) => { e.stopPropagation(); setExpanded(prev => !prev); }}>
                        {expanded ? <KeyboardArrowUpIcon fontSize='small' /> : <KeyboardArrowDownIcon fontSize='small' />}
                    </IconButton>
                </Stack>
            </Stack>

            {/* Expanded fields */}
            <Collapse in={expanded}>
                <Divider />
                <Stack spacing={2.5} p={2.5}>
                    <TextField
                        label='Rebate Name'
                        value={rebate.name}
                        onChange={(e) => onChange({ ...rebate, name: e.target.value })}
                        fullWidth
                        required
                        size='small'
                        variant='outlined'
                    />
                    <TextField
                        select
                        label='Program Type'
                        value={rebate.programType}
                        onChange={(e) => onChange({ ...rebate, programType: e.target.value })}
                        fullWidth
                        size='small'
                        variant='outlined'
                    >
                        {REBATE_PROGRAM_TYPE_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </TextField>
                    <Divider sx={{ mt: 1, mb: 0.5 }} />
                    <Stack spacing={0.75}>
                        <Stack direction='row' spacing={2}>
                            <Typography sx={{ flex: 1, fontSize: '13px', color: 'subtext.main', mt: 0 }}>
                                Period Frequency
                            </Typography>
                            <Typography sx={{ flex: 1, fontSize: '13px', color: 'subtext.main', mt: 0 }}>
                                Rebate Value
                            </Typography>
                        </Stack>

                        <Stack direction='row' spacing={2} alignItems='center'>
                            <Stack flex={1}>
                                <TextField
                                    select
                                    value={rebate.periodType}
                                    onChange={(e) => handlePeriodTypeChange(e.target.value)}
                                    fullWidth
                                    size='small'
                                    variant='outlined'
                                    sx={{ mb: 0 }}
                                    margin='none'
                                >
                                    {PERIOD_OPTIONS.map(opt => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </TextField>
                            </Stack>

                            <Stack flex={1}>
                                <ButtonGroup
                                    variant='outlined'
                                    fullWidth
                                    sx={{ width: '100%' }}
                                >
                                    {REBATE_VALUE_UNIT_OPTIONS.map((option) => (
                                        <Button
                                            key={option.value}
                                            sx={{
                                                flex: 1,
                                                borderColor: 'grey',
                                                backgroundColor: rebateUnit === option.value ? 'blue.main' : 'transparent',
                                                color: rebateUnit === option.value ? 'common.white' : 'blue.main',
                                                '&:hover': {
                                                    backgroundColor: rebateUnit === option.value ? 'blue.dark' : 'action.hover',
                                                },
                                            }}
                                            onClick={() => handleRebateUnitChange(option.value)}
                                        > {option.label}
                                        </Button>
                                    ))}
                                </ButtonGroup>
                            </Stack>
                        </Stack>
                    </Stack>
                    <PeriodCard rebate={rebate} rebateUnit={REBATE_VALUE_UNIT_OPTIONS.find((option) => option.value === rebateUnit)?.label ?? ''} onChange={onChange} onDelete={onDelete} />
                    {MANUAL_PERIOD_TYPES.includes(rebate.periodType) && (
                        <Stack direction='row' spacing={2}>
                            <Button
                                onClick={handleAddPeriod}
                                sx={{
                                    p: 0,
                                    minWidth: 0,
                                    fontSize: '12px',
                                    color: 'blue.main',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    justifyContent: 'flex-start',
                                    width: 'fit-content',
                                }}
                            >
                                Add Period
                            </Button>
                            <Button
                                onClick={handleRemovePeriod}
                                disabled={!canRemovePeriod}
                                sx={{
                                    p: 0,
                                    minWidth: 0,
                                    fontSize: '12px',
                                    color: canRemovePeriod ? 'error.main' : 'subtext.main',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    justifyContent: 'flex-start',
                                    width: 'fit-content',
                                    opacity: canRemovePeriod ? 1 : 0.6,
                                }}
                            >
                                Remove Period
                            </Button>
                        </Stack>
                    )}
                </Stack>
            </Collapse>
        </Paper>
    );
}

export { PERIOD_OPTIONS };
export default RebateCard;
